import { Dispatch } from 'redux';
import fetch from '~/helpers/fetch';
import { BaseResponse } from '~/types/common';
import { EnrichedCustomer } from '~/types/enrich-customer';
import { OfferObject } from '~/types/offerObject';
import { DynamicRTBsResponse, RecommendedRTB, RecommendationsData, TopRecommendationsResponse } from '~/types/products';
import { GdtAnswer } from '~/types/recommendation/single-recommendation';
import { WebContext } from '~/types/web-context';

type AiCartEndpoint = 'top-recommendations' | 'dynamic-rtbs';

export interface AiCartBaseBody {
  webContext: WebContext;
  enrichedCustomer: EnrichedCustomer;
  productList: OfferObject[];
  discoveryToolAnswers?: { answers: GdtAnswer[] };
  contractType?: string;
  mover?: boolean;
}

export type TopRecommendationsBody = AiCartBaseBody & {
  productIds?: never;
};

export type DynamicRtbsBody = AiCartBaseBody & {
  productIds: string[];
};

interface AiCartFetchBaseParams {
  apiVersion: string;
  tracking: Record<string, string>;
  dispatch: Dispatch;
  headers: HeadersInit;
}

interface TopRecommendationsParams extends AiCartFetchBaseParams {
  body: TopRecommendationsBody;
}

interface DynamicRtbsParams extends AiCartFetchBaseParams {
  body: DynamicRtbsBody;
}

const postAiCart = async <TResponse extends BaseResponse>({
  apiVersion,
  endpoint,
  body,
  tracking,
  dispatch,
  headers,
}: AiCartFetchBaseParams & {
  endpoint: AiCartEndpoint;
  body: TopRecommendationsBody | DynamicRtbsBody;
}): Promise<TResponse | null> => {
  try {
    const response = await fetch<TResponse>({
      endpoint: `${apiVersion}/aicart/${endpoint}`,
      options: {
        method: 'POST',
        body: JSON.stringify(body),
        headers,
      },
      tracking,
      dispatch,
    });

    return response;
  } catch {
    return null;
  }
};

export const fetchTopRecommendations = async ({
  apiVersion,
  body,
  tracking,
  dispatch,
  headers,
}: TopRecommendationsParams): Promise<RecommendationsData | null> => {
  const response = await postAiCart<TopRecommendationsResponse>({
    apiVersion,
    endpoint: 'top-recommendations',
    body,
    tracking,
    dispatch,
    headers,
  });

  if (response?.success && response.recommendedProducts) {
    return response.recommendedProducts;
  }

  return null;
};

export const fetchDynamicRtbs = async ({
  apiVersion,
  body,
  tracking,
  dispatch,
  headers,
}: DynamicRtbsParams): Promise<Record<string, RecommendedRTB> | null> => {
  const response = await postAiCart<DynamicRTBsResponse>({
    apiVersion,
    endpoint: 'dynamic-rtbs',
    body,
    tracking,
    dispatch,
    headers,
  });

  if (response?.success && response.recommendedRTBs) {
    return response.recommendedRTBs;
  }

  return null;
};
